/**
 * The wire protocol for `/ws/core/`.
 *
 * The server is `backend/src/baserow/ws/consumers.py`; the replay half is
 * `backend/src/baserow/ws/realtime_events.py`. Everything here has to agree with
 * those two, so each type names the field it mirrors rather than paraphrasing it.
 */

/**
 * Cursor sentinels for `replay_events`. These must match `FIRST_CONNECT_CURSOR`
 * and `NO_REPLAY_AVAILABLE` in `backend/src/baserow/ws/realtime_events.py`.
 *
 * `FIRST_CONNECT` is the opening position of a session and asks for a baseline
 * only. `NO_REPLAY_AVAILABLE` says this client reconnected with no usable
 * high-water mark, which the server answers with `force_refresh`. Anything the
 * server sees below `NO_REPLAY_AVAILABLE` it normalises back to `FIRST_CONNECT`.
 */
export const FIRST_CONNECT_CURSOR = -1
export const NO_REPLAY_AVAILABLE = -2

/**
 * Every message the server sends.
 *
 * `_event_id` is injected by `RealtimeEventHandler.add_event_id_to_payload` and is
 * absent on messages the server synthesises rather than replays -- `authentication`
 * and `replay_events_result` among them.
 */
export interface RealtimeMessage {
  type: string
  _event_id?: number
  [key: string]: unknown
}

/** First message on every connection, sent before the server closes an unauthenticated socket. */
export interface AuthenticationMessage extends RealtimeMessage {
  type: 'authentication'
  success: boolean
  web_socket_id: string
  /**
   * Whether the server is recording events at all. False means replay is
   * unavailable for the whole connection, not merely that there is nothing to
   * replay, so the cursor moves to `NO_REPLAY_AVAILABLE` and stays there.
   */
  replay_enabled: boolean
}

/** The server's answer to a `replay_events` request. */
export interface ReplayEventsResultMessage extends RealtimeMessage {
  type: 'replay_events_result'
  /** The gap was too large, the cursor expired, or a replay failed mid-flight. */
  force_refresh: boolean
  /**
   * The newest event the server knows about. Legitimately `0` when nothing has
   * been recorded yet, so it is a number to compare, not a value to test for
   * truthiness.
   */
  latest_event_id: number
}

/** Sent to every other connection of a user whose access was revoked. */
export interface ForceDisconnectMessage extends RealtimeMessage {
  type: 'force_disconnect'
}

/** What the client sends to start receiving updates for a page. */
export interface PageSubscription {
  /** `null` means the empty page, which the server reads as unsubscribing from all. */
  page: string | null
  parameters: Record<string, string | number>
}

export function isAuthenticationMessage(
  message: RealtimeMessage,
): message is AuthenticationMessage {
  return message.type === 'authentication'
}

export function isReplayEventsResultMessage(
  message: RealtimeMessage,
): message is ReplayEventsResultMessage {
  return message.type === 'replay_events_result'
}

/**
 * Parses a frame, returning null for anything that is not a typed object.
 *
 * The server only ever sends JSON objects carrying a string `type`, so a frame
 * that is not one is either a proxy injecting something or a protocol change
 * nobody told the client about. Dropping it is better than dispatching a message
 * whose `type` is a number and matching no handler in a way nobody can see.
 */
export function parseMessage(raw: unknown): RealtimeMessage | null {
  if (typeof raw !== 'string') return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
  const message = parsed as Record<string, unknown>
  if (typeof message.type !== 'string') return null

  return message as RealtimeMessage
}

/**
 * Advances the replay cursor past a message, if that message carries it forward.
 *
 * Every inbound frame goes through this before it is dispatched, so the cursor
 * reflects what the client has actually seen rather than what it has handled.
 * A message with no `_event_id`, or one that moves backwards, leaves it alone.
 */
export function nextCursor(cursor: number, message: RealtimeMessage): number {
  const id = message._event_id
  if (typeof id !== 'number' || id <= cursor) return cursor
  return id
}
